import type { RepeatMode } from './sonosPlayMode';

export interface LocalQueueItem {
  id: string;
  uri: string;
  title: string;
  artist: string;
  album?: string;
  image_url?: string | null;
  durationMs?: number;
}

export interface LocalQueueState {
  items: LocalQueueItem[];
  currentIndex: number;
  shuffle: boolean;
  repeat: RepeatMode;
}

export function createEmptyLocalQueue(): LocalQueueState {
  return { items: [], currentIndex: -1, shuffle: false, repeat: 'off' };
}

let _seq = 0;
export function newLocalQueueItemId(): string {
  _seq += 1;
  return `lq-${Date.now()}-${_seq}`;
}

export function buildLocalQueueItem(input: {
  uri: string;
  title?: string;
  artist?: string;
  album?: string;
  image_url?: string | null;
  durationMs?: number;
}): LocalQueueItem {
  return {
    id: newLocalQueueItemId(),
    uri: input.uri,
    title: input.title?.trim() || 'Track',
    artist: input.artist?.trim() || '',
    album: input.album,
    image_url: input.image_url ?? null,
    durationMs: input.durationMs,
  };
}

/** Avanza índice tras fin de tema / next. Devuelve null si no hay siguiente. */
export function advanceLocalQueueIndex(state: LocalQueueState): number | null {
  const { items, currentIndex, repeat } = state;
  if (!items.length || currentIndex < 0) return null;
  if (repeat === 'one') return currentIndex;
  const next = currentIndex + 1;
  if (next < items.length) return next;
  if (repeat === 'all') return 0;
  return null;
}

/** URIs que siguen al tema actual (para precargar en Spotify Connect). */
export function peekLocalQueueFollowing(state: LocalQueueState, count = 2): string[] {
  const { items, currentIndex, repeat } = state;
  if (!items.length || currentIndex < 0 || count <= 0) return [];
  if (repeat === 'one') {
    const uri = items[currentIndex]?.uri;
    return uri ? Array.from({ length: count }, () => uri) : [];
  }
  const out: string[] = [];
  let i = currentIndex;
  for (let n = 0; n < count; n += 1) {
    i += 1;
    if (i >= items.length) {
      if (repeat === 'all') i = 0;
      else break;
    }
    const uri = items[i]?.uri;
    if (uri) out.push(uri);
  }
  return out;
}

export function previousLocalQueueIndex(state: LocalQueueState, positionMs: number): number | null {
  const { items, currentIndex, repeat } = state;
  if (!items.length || currentIndex < 0) return null;
  if (positionMs > 3000) return currentIndex;
  if (currentIndex > 0) return currentIndex - 1;
  if (repeat === 'all') return items.length - 1;
  return currentIndex;
}

export function reorderLocalQueue(
  state: LocalQueueState,
  from: number,
  to: number,
): LocalQueueState {
  if (from === to || from < 0 || to < 0 || from >= state.items.length || to >= state.items.length) {
    return state;
  }
  const items = [...state.items];
  const [moved] = items.splice(from, 1);
  items.splice(to, 0, moved);
  let currentIndex = state.currentIndex;
  if (currentIndex === from) currentIndex = to;
  else if (from < currentIndex && to >= currentIndex) currentIndex -= 1;
  else if (from > currentIndex && to <= currentIndex) currentIndex += 1;
  return { ...state, items, currentIndex };
}

export function removeLocalQueueItem(state: LocalQueueState, index: number): LocalQueueState {
  if (index < 0 || index >= state.items.length) return state;
  const items = state.items.filter((_, i) => i !== index);
  let currentIndex = state.currentIndex;
  if (index === currentIndex) {
    currentIndex = items.length ? Math.min(index, items.length - 1) : -1;
  } else if (index < currentIndex) {
    currentIndex -= 1;
  }
  return { ...state, items, currentIndex };
}

export function shuffleLocalQueueOrder(state: LocalQueueState): LocalQueueState {
  if (state.items.length < 2) return { ...state, shuffle: true };
  const current = state.currentIndex >= 0 ? state.items[state.currentIndex] : null;
  const rest = state.items.filter((_, i) => i !== state.currentIndex);
  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  const items = current ? [current, ...rest] : rest;
  return { ...state, items, currentIndex: current ? 0 : -1, shuffle: true };
}
